import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { format } from 'date-fns';
import PageHeader from '../components/common/PageHeader';
import AngebotStatusPipeline from '../components/common/AngebotStatusPipeline';
import DokumentenAblage from '../components/common/DokumentenAblage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useNotification } from '../hooks/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import angeboteService from '../services/angeboteService';
import { kundenService } from '../services/kundenService';
import { ansprechpartnerService } from '../services/ansprechpartnerService';
import { Angebot, AngebotCreate, AngebotspositionArt, AngebotspositionCreate, Ansprechpartner, Kunde, Steuerfall } from '../types';
import { STEUERFALL_OPTIONEN, steuersatzFuer, steuerfallHinweis, vorschlageSteuerfall } from '../utils/steuerfall';
import { isFestpreisProjektTyp } from '../utils/projektTyp';
import AngebotMarkupField from '../components/common/AngebotMarkupField';

const todayIso = () => format(new Date(), 'yyyy-MM-dd');

const standardSchlusstext = (gueltigBis?: string | null): string => {
  const teile: string[] = [];
  if (gueltigBis && /^\d{4}-\d{2}-\d{2}$/.test(gueltigBis)) {
    const [jahr, monat, tag] = gueltigBis.split('-');
    teile.push(`An dieses Angebot halten wir uns bis zum ${tag}.${monat}.${jahr} gebunden.`);
  }
  teile.push('Wir würden uns sehr freuen, wenn Ihnen unser Angebot zusagt und wir mit Ihrem Auftrag rechnen können.');
  teile.push('Für Rückfragen stehen wir Ihnen gerne per E-Mail oder telefonisch zur Verfügung.');
  return teile.join('\n\n');
};

type DraftPosition = AngebotspositionCreate & { clientId: string };
type AngebotForm = Omit<AngebotCreate, 'positionen'> & { positionen: DraftPosition[] };

const newClientId = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `pos-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const withOrder = (positionen: DraftPosition[]): DraftPosition[] => (
  positionen.map((pos, index) => ({ ...pos, sort_order: index + 1 }))
);

const isLeer = (pos: { art?: AngebotspositionArt }) => pos.art === 'leer';

const emptyPosition = (sort: number, tagessatz = 0, einheit = 'PT'): DraftPosition => ({
  clientId: newClientId(),
  art: 'position',
  beschreibung: '',
  menge: 1,
  einheit,
  einzelpreis: einheit === 'Pauschal' ? 0 : tagessatz,
  gesamtpreis: einheit === 'Pauschal' ? 0 : tagessatz,
  sort_order: sort,
});

const emptyLeer = (sort: number): DraftPosition => ({
  clientId: newClientId(),
  art: 'leer',
  beschreibung: '',
  menge: 0,
  einheit: '',
  einzelpreis: 0,
  gesamtpreis: 0,
  sort_order: sort,
});

const AngebotDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const isNew = id === 'neu';

  const [angebot, setAngebot] = useState<Angebot | null>(null);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [ansprechpartner, setAnsprechpartner] = useState<Ansprechpartner[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [insertMenu, setInsertMenu] = useState<{ anchor: HTMLElement; index: number } | null>(null);
  const [formData, setFormData] = useState<AngebotForm>({
    angebotsnummer: '',
    titel: '',
    beschreibung: '',
    angebotsdatum: todayIso(),
    gueltig_bis: '',
    projekt_typ: 'Single',
    personentage: undefined,
    tagessatz: undefined,
    stunden_pro_pt: 8,
    nettobetrag: 0,
    steuersatz: 19,
    steuerfall: 'inland',
    steuerbetrag: 0,
    bruttobetrag: 0,
    anrede: 'Sehr geehrte Damen und Herren,',
    anschreiben: '',
    grussformel: 'Mit freundlichen Grüßen',
    schlusstext: standardSchlusstext(''),
    kunde_id: parseInt(searchParams.get('kunde_id') || '0', 10) || 0,
    ansprechpartner_id: null,
    positionen: [],
  });

  const isDraft = isNew || angebot?.status === 'Entwurf';
  const canEdit = isDraft && hasPermission(P.ANGEBOTE_EDIT);
  const canCreate = hasPermission(P.ANGEBOTE_CREATE);
  const canStatus = hasPermission(P.ANGEBOTE_STATUS);
  const canDelete = hasPermission(P.ANGEBOTE_DELETE);
  const canProjekt = hasPermission(P.ANGEBOTE_EDIT) && hasPermission(P.PROJEKTE_CREATE);
  const editable = isNew ? canCreate : canEdit;

  const totals = useMemo(() => {
    const netto = (formData.positionen || [])
      .filter((p) => !isLeer(p))
      .reduce((s, p) => s + (p.gesamtpreis || 0), 0);
    const satz = formData.steuersatz || 0;
    const steuer = Math.round(netto * satz) / 100;
    return { nettobetrag: netto, steuerbetrag: steuer, bruttobetrag: netto + steuer };
  }, [formData.positionen, formData.steuersatz]);

  const expired = Boolean(
    !isNew && angebot?.status === 'Gesendet' && formData.gueltig_bis && formData.gueltig_bis < todayIso(),
  );

  const load = useCallback(async () => {
    const list = await kundenService.list();
    setKunden(list);
    if (isNew) {
      const nr = await angeboteService.getNextNumber();
      setFormData((prev) => ({ ...prev, angebotsnummer: nr }));
      return;
    }
    if (!id) return;
    const data = await angeboteService.get(parseInt(id, 10));
    setAngebot(data);
    setFormData({
      angebotsnummer: data.angebotsnummer,
      titel: data.titel,
      beschreibung: data.beschreibung || '',
      angebotsdatum: data.angebotsdatum,
      gueltig_bis: data.gueltig_bis || '',
      projekt_typ: data.projekt_typ,
      personentage: data.personentage ?? undefined,
      tagessatz: data.tagessatz ?? undefined,
      stunden_pro_pt: data.stunden_pro_pt,
      nettobetrag: data.nettobetrag,
      steuersatz: data.steuersatz,
      steuerfall: data.steuerfall || 'inland',
      steuerbetrag: data.steuerbetrag,
      bruttobetrag: data.bruttobetrag,
      anrede: data.anrede || '',
      anschreiben: data.anschreiben || '',
      grussformel: data.grussformel || '',
      schlusstext: data.schlusstext?.trim() ? data.schlusstext : standardSchlusstext(data.gueltig_bis),
      kunde_id: data.kunde_id,
      ansprechpartner_id: data.ansprechpartner_id || null,
      positionen: [...data.positionen]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((p) => ({
          clientId: newClientId(),
          art: p.art || 'position',
          beschreibung: p.beschreibung,
          menge: p.menge,
          einheit: p.einheit,
          einzelpreis: p.einzelpreis,
          gesamtpreis: p.gesamtpreis,
          sort_order: p.sort_order,
        })),
    });
  }, [id, isNew]);

  useEffect(() => {
    load().catch(() => showError('Angebot konnte nicht geladen werden'));
  }, [load, showError]);

  useEffect(() => {
    if (!formData.kunde_id) {
      setAnsprechpartner([]);
      return;
    }
    ansprechpartnerService.list({ kunde_id: formData.kunde_id }).then(setAnsprechpartner).catch(() => setAnsprechpartner([]));
  }, [formData.kunde_id]);

  const setField = <K extends keyof AngebotCreate>(key: K, value: AngebotCreate[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleKundeChange = (kundeId: number) => {
    const kunde = kunden.find((k) => k.id === kundeId);
    const fall = vorschlageSteuerfall(kunde?.land, kunde?.ust_id);
    setFormData((prev) => ({
      ...prev,
      kunde_id: kundeId,
      ansprechpartner_id: null,
      steuerfall: fall,
      steuersatz: steuersatzFuer(fall),
    }));
  };

  const updatePosition = (index: number, patch: Partial<DraftPosition>) => {
    const pos = [...(formData.positionen || [])];
    const next = { ...pos[index], ...patch };
    if (isLeer(next)) {
      next.menge = 0;
      next.einheit = '';
      next.einzelpreis = 0;
      next.gesamtpreis = 0;
    } else {
      next.gesamtpreis = Math.round((next.menge || 0) * (next.einzelpreis || 0) * 100) / 100;
    }
    pos[index] = next;
    setFormData((prev) => ({ ...prev, positionen: pos }));
  };

  const einheitFuerNeuePosition = (projektTyp: string | undefined) => (
    isFestpreisProjektTyp(projektTyp) ? 'Pauschal' : 'PT'
  );

  const appendPosition = (kind: AngebotspositionArt) => {
    setFormData((prev) => {
      const current = prev.positionen || [];
      const item = kind === 'leer'
        ? emptyLeer(current.length + 1)
        : emptyPosition(current.length + 1, prev.tagessatz || 0, einheitFuerNeuePosition(prev.projekt_typ));
      return { ...prev, positionen: withOrder([...current, item]) };
    });
  };

  const insertAt = (index: number, kind: AngebotspositionArt) => {
    setFormData((prev) => {
      const current = [...(prev.positionen || [])];
      const item = kind === 'leer'
        ? emptyLeer(index + 1)
        : emptyPosition(index + 1, prev.tagessatz || 0, einheitFuerNeuePosition(prev.projekt_typ));
      current.splice(index, 0, item);
      return { ...prev, positionen: withOrder(current) };
    });
    setInsertMenu(null);
  };

  const removeAt = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      positionen: withOrder((prev.positionen || []).filter((_, i) => i !== index)),
    }));
  };

  const onDragEnd = (result: DropResult) => {
    if (!editable || !result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    setFormData((prev) => {
      const items = [...(prev.positionen || [])];
      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);
      return { ...prev, positionen: withOrder(items) };
    });
  };

  const payload = (): AngebotCreate => ({
    ...formData,
    gueltig_bis: formData.gueltig_bis || null,
    ansprechpartner_id: formData.ansprechpartner_id || null,
    positionen: (formData.positionen || []).map((pos, index) => ({
      beschreibung: pos.beschreibung,
      menge: pos.menge,
      einheit: pos.einheit,
      einzelpreis: pos.einzelpreis,
      gesamtpreis: pos.gesamtpreis,
      art: pos.art || 'position',
      sort_order: index + 1,
    })),
    ...totals,
  });

  const persist = async (): Promise<boolean> => {
    if (!formData.titel.trim() || !formData.kunde_id) {
      showError('Titel und Kunde sind Pflichtfelder.');
      return false;
    }
    if (isNew) {
      const created = await angeboteService.create(payload());
      showSuccess('Angebot erstellt');
      navigate(`/angebote/${created.id}`, { replace: true });
      return true;
    }
    if (!id) return false;
    const updated = await angeboteService.update(parseInt(id, 10), payload());
    setAngebot(updated);
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await persist();
      if (ok && !isNew) showSuccess('Angebot gespeichert');
    } catch {
      showError('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const exportDokument = async (art: 'pdf' | 'docx') => {
    if (!id || isNew) return;
    setSaving(true);
    try {
      if (editable) {
        const ok = await persist();
        if (!ok) return;
      }
      const angebotId = parseInt(id, 10);
      if (art === 'pdf') await angeboteService.generatePdf(angebotId);
      else await angeboteService.generateDocx(angebotId);
    } catch (e) {
      showError((e as Error).message || (art === 'pdf' ? 'PDF fehlgeschlagen' : 'Word-Export fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (status: string) => {
    if (!id || isNew) return;
    try {
      const updated = await angeboteService.updateStatus(parseInt(id, 10), status);
      setAngebot(updated);
      showSuccess(`Status: ${updated.status === 'Zurueckgezogen' ? 'Zurückgezogen' : updated.status}`);
    } catch (err) {
      showError((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Statuswechsel fehlgeschlagen');
    }
  };

  const handleProjekt = async () => {
    if (!id || isNew || !angebot) return;
    if (angebot.projekt_id) {
      navigate(`/projekte/${angebot.projekt_id}`);
      return;
    }
    try {
      const projekt = await angeboteService.createProjekt(parseInt(id, 10));
      showSuccess('Projekt erzeugt');
      navigate(`/projekte/${projekt.id}`);
    } catch (err) {
      showError((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Projekt konnte nicht erzeugt werden');
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    try {
      await angeboteService.delete(parseInt(id, 10));
      showSuccess('Angebot gelöscht');
      navigate('/angebote');
    } catch (err) {
      showError((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <Box>
      <PageHeader
        title={isNew ? 'Neues Angebot' : (angebot?.angebotsnummer || 'Angebot')}
        breadcrumbs={[{ label: 'Angebote', path: '/angebote' }, { label: isNew ? 'Neu' : (angebot?.angebotsnummer || '') }]}
        actions={(
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {!isNew && (
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => exportDokument('pdf')} disabled={saving}>
                PDF
              </Button>
            )}
            {!isNew && (
              <Button variant="outlined" startIcon={<DescriptionOutlinedIcon />} onClick={() => exportDokument('docx')} disabled={saving}>
                Word
              </Button>
            )}
            {!isNew && canProjekt && angebot && ['Gesendet', 'Gewonnen'].includes(angebot.status) && (
              <Button variant="outlined" onClick={handleProjekt}>
                {angebot.projekt_id ? 'Zum Projekt' : 'Projekt erzeugen'}
              </Button>
            )}
            {editable && (
              <Button variant="contained" onClick={handleSave} disabled={saving}>Speichern</Button>
            )}
            {!isNew && canDelete && angebot?.status === 'Entwurf' && (
              <Button color="error" onClick={() => setDeleteOpen(true)}>Löschen</Button>
            )}
          </Box>
        )}
      />

      {expired && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Die Gültigkeit dieses Angebots ist abgelaufen ({formData.gueltig_bis ? format(new Date(formData.gueltig_bis), 'dd.MM.yyyy') : ''}).
        </Alert>
      )}

      {!isNew && angebot && (
        <AngebotStatusPipeline
          currentStatus={angebot.status}
          onStatusChange={canStatus ? handleStatus : undefined}
        />
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Angebotsnummer" value={formData.angebotsnummer} onChange={(e) => setField('angebotsnummer', e.target.value)} disabled={!editable} required />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth label="Titel" value={formData.titel} onChange={(e) => setField('titel', e.target.value)} disabled={!editable} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Kunde" value={formData.kunde_id || ''} onChange={(e) => handleKundeChange(Number(e.target.value))} disabled={!editable} required>
              <MenuItem value={0} disabled>Bitte wählen</MenuItem>
              {kunden.map((k) => (
                <MenuItem key={k.id} value={k.id}>{k.name_des_unternehmens}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Ansprechpartner" value={formData.ansprechpartner_id || ''} onChange={(e) => setField('ansprechpartner_id', e.target.value ? Number(e.target.value) : null)} disabled={!editable}>
              <MenuItem value="">–</MenuItem>
              {ansprechpartner.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.vorname} {a.nachname}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="date" label="Angebotsdatum" InputLabelProps={{ shrink: true }} value={formData.angebotsdatum} onChange={(e) => setField('angebotsdatum', e.target.value)} disabled={!editable} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="date" label="Gültig bis" InputLabelProps={{ shrink: true }} value={formData.gueltig_bis || ''} onChange={(e) => {
              const next = e.target.value;
              setFormData((prev) => {
                const bisher = standardSchlusstext(prev.gueltig_bis);
                const unveraendert = !prev.schlusstext?.trim() || prev.schlusstext.trim() === bisher.trim();
                return { ...prev, gueltig_bis: next, schlusstext: unveraendert ? standardSchlusstext(next) : prev.schlusstext };
              });
            }} disabled={!editable} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="Projekttyp" value={formData.projekt_typ} onChange={(e) => setField('projekt_typ', e.target.value)} disabled={!editable}>
              <MenuItem value="Single">Single</MenuItem>
              <MenuItem value="Kontingent">Kontingent</MenuItem>
              <MenuItem value="Festpreis">Festpreis</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="Steuerfall" value={formData.steuerfall || 'inland'} onChange={(e) => {
              const fall = e.target.value as Steuerfall;
              setFormData((prev) => ({ ...prev, steuerfall: fall, steuersatz: steuersatzFuer(fall) }));
            }} disabled={!editable}>
              {STEUERFALL_OPTIONEN.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {steuerfallHinweis(formData.steuerfall) && (
            <Grid item xs={12}>
              <Alert severity="info">{steuerfallHinweis(formData.steuerfall)}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <AngebotMarkupField
              label="Beschreibung / Nachfrage"
              value={formData.beschreibung || ''}
              onChange={(value) => setField('beschreibung', value)}
              disabled={!editable}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Anrede" value={formData.anrede || ''} onChange={(e) => setField('anrede', e.target.value)} disabled={!editable} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Grußformel" value={formData.grussformel || ''} onChange={(e) => setField('grussformel', e.target.value)} disabled={!editable} />
          </Grid>
          <Grid item xs={12}>
            <AngebotMarkupField
              label="Anschreiben"
              value={formData.anschreiben || ''}
              onChange={(value) => setField('anschreiben', value)}
              disabled={!editable}
              minRows={6}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6">Positionen</Typography>
            {editable && (
              <Typography variant="body2" color="text.secondary">
                Ziehen ändert die Reihenfolge. Leerpositionen gliedern die Liste und zählen nicht zum Betrag; ein Text wird als Überschrift gedruckt.
              </Typography>
            )}
          </Box>
          {editable && (
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button startIcon={<HorizontalRuleIcon />} onClick={() => appendPosition('leer')}>
                Leerposition
              </Button>
              <Button startIcon={<AddIcon />} onClick={() => appendPosition('position')}>
                Position
              </Button>
            </Box>
          )}
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              {editable && <TableCell sx={{ width: 40 }} />}
              <TableCell>Beschreibung</TableCell>
              <TableCell sx={{ width: 160, minWidth: 160 }}>Menge</TableCell>
              <TableCell width={130}>Einheit</TableCell>
              <TableCell width={120}>Einzelpreis</TableCell>
              <TableCell width={120} align="right">Gesamt</TableCell>
              {editable && <TableCell width={88} />}
            </TableRow>
          </TableHead>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="angebotspositionen">
              {(dropProvided) => (
                <TableBody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {(formData.positionen || []).map((pos, idx) => (
                    <Draggable key={pos.clientId} draggableId={pos.clientId} index={idx} isDragDisabled={!editable}>
                      {(dragProvided, dragSnapshot) => (
                        <TableRow
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          sx={{
                            ...(isLeer(pos) ? { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' } : {}),
                            ...(dragSnapshot.isDragging ? { display: 'table', bgcolor: theme.palette.mode === 'dark' ? 'rgba(61,212,126,0.12)' : 'rgba(61,212,126,0.08)' } : {}),
                          }}
                        >
                          {editable && (
                            <TableCell sx={{ px: 0.5, color: 'text.secondary' }}>
                              <Box
                                component="span"
                                {...dragProvided.dragHandleProps}
                                role="button"
                                tabIndex={0}
                                aria-label="Position verschieben"
                                sx={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center', p: 0.5, borderRadius: 1 }}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </Box>
                            </TableCell>
                          )}
                          {isLeer(pos) ? (
                            <TableCell colSpan={5}>
                              {editable ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  multiline
                                  minRows={1}
                                  value={pos.beschreibung}
                                  placeholder="Optional als Überschrift – eine Leerzeile gliedert den Text"
                                  onChange={(e) => updatePosition(idx, { beschreibung: e.target.value })}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: pos.beschreibung.trim() ? 700 : 400, fontStyle: pos.beschreibung.trim() ? 'normal' : 'italic', color: pos.beschreibung.trim() ? 'text.primary' : 'text.secondary', minHeight: 24 }}
                                >
                                  {pos.beschreibung.trim() || 'Leerzeile'}
                                </Typography>
                              )}
                            </TableCell>
                          ) : (
                            <>
                              <TableCell>
                                <TextField fullWidth size="small" value={pos.beschreibung} onChange={(e) => updatePosition(idx, { beschreibung: e.target.value })} disabled={!editable} />
                              </TableCell>
                              <TableCell sx={{ width: 160, minWidth: 160 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  value={pos.menge}
                                  onChange={(e) => updatePosition(idx, { menge: e.target.value === '' ? 0 : Number(e.target.value) })}
                                  disabled={!editable}
                                  inputProps={{ step: 0.5, min: 0 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField select size="small" value={pos.einheit} onChange={(e) => updatePosition(idx, { einheit: e.target.value })} disabled={!editable}>
                                  <MenuItem value="PT">PT</MenuItem>
                                  <MenuItem value="Stunden">Stunden</MenuItem>
                                  <MenuItem value="Pauschal">Pauschal</MenuItem>
                                </TextField>
                              </TableCell>
                              <TableCell>
                                <TextField size="small" type="number" value={pos.einzelpreis} onChange={(e) => updatePosition(idx, { einzelpreis: Number(e.target.value) })} disabled={!editable} />
                              </TableCell>
                              <TableCell align="right">{pos.gesamtpreis.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</TableCell>
                            </>
                          )}
                          {editable && (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title="Darunter einfügen">
                                <IconButton
                                  size="small"
                                  aria-label="Darunter einfügen"
                                  onClick={(event) => setInsertMenu({ anchor: event.currentTarget, index: idx + 1 })}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Entfernen">
                                <IconButton size="small" aria-label="Position entfernen" onClick={() => removeAt(idx)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                  {(formData.positionen || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={editable ? 7 : 5}>Noch keine Positionen – Nachfrage kann ohne Positionen erfasst werden.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        </Table>
        <Menu
          anchorEl={insertMenu?.anchor}
          open={Boolean(insertMenu)}
          onClose={() => setInsertMenu(null)}
        >
          <MenuItem onClick={() => insertMenu && insertAt(insertMenu.index, 'position')}>Position darunter</MenuItem>
          <MenuItem onClick={() => insertMenu && insertAt(insertMenu.index, 'leer')}>Leerposition darunter</MenuItem>
        </Menu>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, mt: 2 }}>
          <Typography>Netto: {totals.nettobetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</Typography>
          <Typography>Steuer: {totals.steuerbetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</Typography>
          <Typography fontWeight={700}>Brutto: {totals.bruttobetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</Typography>
        </Box>
        <Box sx={{ mt: 3 }}>
          <AngebotMarkupField
            label="Text unter der Tabelle"
            value={formData.schlusstext || ''}
            onChange={(value) => setField('schlusstext', value)}
            disabled={!editable}
            minRows={5}
          />
        </Box>
      </Paper>

      {!isNew && id && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Dokumente</Typography>
          <DokumentenAblage angebotId={parseInt(id, 10)} />
        </Paper>
      )}

      {!isNew && angebot?.projekt_id && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Verknüpftes Projekt:{' '}
          <Button size="small" onClick={() => navigate(`/projekte/${angebot.projekt_id}`)}>
            {angebot.projekt_name || `#${angebot.projekt_id}`}
          </Button>
        </Alert>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Angebot löschen"
        message="Möchten Sie dieses Angebot wirklich löschen?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
};

export default AngebotDetailPage;
